import React, { useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { t } from "i18next";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from "@mui/icons-material";
import { selectAllFoldersForTree } from "../../../../../../../redux-react/reducers/foldersReducer";

const FolderSelectDropdown = ({
	onFolderIdSelect,
	label,
	selectedFolderId: externalSelectedId,
	includeSubfolders = true
}) => {
	const foldersData = useSelector(selectAllFoldersForTree);
	const [internalSelectedId, setInternalSelectedId] = useState(externalSelectedId || "");
	const [openFolders, setOpenFolders] = useState({});
	const [isSelectFolderOpen, setIsSelectFolderOpen] = useState(false);

	const formattedFolders = useMemo(() => {
		const formatFolderTree = (folders, parentId = null) => {
			return folders
				.filter(folder => folder.parentId === parentId)
				.map(folder => ({
					id: folder.id,
					name: folder.name,
					isOpen: false,
					isSelected: false,
					children: formatFolderTree(folders, folder.id)
				}));
		};
		return formatFolderTree(foldersData || []);
	}, [foldersData]);

	const getAllSubfolderIds = useCallback(
		folderId => {
			if (!folderId || !foldersData || foldersData.length === 0) return [folderId];

			const result = [folderId];

			const findChildren = parentId => {
				const children = foldersData.filter(folder => folder.parentId === parentId);

				children.forEach(child => {
					result.push(child.id);
					findChildren(child.id);
				});
			};

			findChildren(folderId);
			return result;
		},
		[foldersData]
	);

	React.useEffect(() => {
		if (externalSelectedId !== undefined) {
			setInternalSelectedId(externalSelectedId);
		}
	}, [externalSelectedId]);

	const toggleFolder = folderId => {
		setOpenFolders(prev => ({
			...prev,
			[folderId]: !prev[folderId]
		}));
	};

	const handleSelectOpen = () => {
		setIsSelectFolderOpen(true);
	};

	const handleSelectClose = () => {
		setIsSelectFolderOpen(false);
	};

	const handleFolderSelection = folderId => {
		setInternalSelectedId(folderId);

		if (folderId === "") {
			onFolderIdSelect(null, null);
		} else if (includeSubfolders) {
			const allFolderIds = getAllSubfolderIds(folderId);
			onFolderIdSelect(folderId, allFolderIds);
		} else {
			onFolderIdSelect(folderId, [folderId]);
		}

		setIsSelectFolderOpen(false);
	};

	const handleChange = event => {
		const folderId = event.target.value;
		handleFolderSelection(folderId);
	};

	const renderFolderItems = (folders, depth = 0) => {
		return folders.map(folder => (
			<Box key={folder.id}>
				<MenuItem
					value={folder.id}
					sx={{
						pl: 2 + depth * 2,
						display: "flex",
						alignItems: "center",
						minHeight: "48px"
					}}
					onClick={e => {
						e.stopPropagation();
						handleFolderSelection(folder.id);
					}}
				>
					<Box sx={{ flexGrow: 1 }}>{folder.name}</Box>
					{folder.children && folder.children.length > 0 && (
						<IconButton
							size="small"
							onClick={e => {
								e.stopPropagation();
								toggleFolder(folder.id);
							}}
						>
							{openFolders[folder.id] ? (
								<ExpandLessIcon fontSize="small" />
							) : (
								<ExpandMoreIcon fontSize="small" />
							)}
						</IconButton>
					)}
				</MenuItem>
				{folder.children && folder.children.length > 0 && (
					<Collapse in={openFolders[folder.id]} timeout="auto" unmountOnExit>
						{renderFolderItems(folder.children, depth + 1)}
					</Collapse>
				)}
			</Box>
		));
	};

	const getFolderPath = () => {
		if (!internalSelectedId) return t("SOURCES.FOLDER.selectFolder");

		const findPath = (folders, targetId, currentPath = []) => {
			// eslint-disable-next-line no-restricted-syntax
			for (const folder of folders) {
				const newPath = [...currentPath, folder.name];
				if (folder.id === targetId) {
					return newPath.join(" / ");
				}
				if (folder.children) {
					const foundPath = findPath(folder.children, targetId, newPath);
					if (foundPath) return foundPath;
				}
			}
			return null;
		};

		const path = findPath(formattedFolders, internalSelectedId);
		return path || t("SOURCES.FOLDER.selectFolder");
	};

	return (
		<FormControl fullWidth>
			<InputLabel>{label || t("SOURCES.FOLDER.selectFolder")}</InputLabel>
			<Select
				value={internalSelectedId}
				onChange={handleChange}
				onOpen={handleSelectOpen}
				onClose={handleSelectClose}
				open={isSelectFolderOpen}
				label={label || t("SOURCES.FOLDER.selectFolder")}
				renderValue={() => getFolderPath()}
				MenuProps={{
					PaperProps: {
						style: {
							maxHeight: 400,
							width: 300
						}
					}
				}}
				endAdornment={
					<Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
						<ExpandMoreIcon />
					</Box>
				}
			>
				<MenuItem value="">
					<em>{t("SOURCES.FOLDER.clearSelection")}</em>
				</MenuItem>
				{renderFolderItems(formattedFolders)}
			</Select>
		</FormControl>
	);
};

export default FolderSelectDropdown;
